import { MASCOT_PNG } from '../constants/brandAssets';
import type { AppLanguage } from '../i18n';

export interface FAQItem {
  question: string;
  answer: string;
}

export const LANDING_SITE_URL =
  (process.env.REACT_APP_SITE_URL || 'https://elcantodelnabo.com').replace(/\/$/, '');

export interface PageSeo {
  title: string;
  description: string;
  ogImagePath?: string;
}

/** Keys de las paginas publicas indexables con variante por idioma. */
export type LandingPageKey = 'home' | 'turnos';

interface LangPaths {
  es: string;
  en: string;
}

/** Mapa de rutas publicas por idioma (para canonical + hreflang). */
export const LANDING_PAGE_PATHS: Record<LandingPageKey, LangPaths> = {
  home: { es: '/', en: '/en' },
  turnos: {
    es: '/sistema-administracion-turnos-karaoke',
    en: '/en/karaoke-queue-management-system',
  },
};

/** SEO de la home por idioma. Las paginas long-tail pasan su propio PageSeo. */
export const LANDING_SEO_BY_LANG: Record<AppLanguage, PageSeo> = {
  es: {
    title:
      'Canto del Nabo | Sistema de administración de turnos para karaoke en México',
    description:
      'Sistema de administración de turnos para karaoke en México: cola de canciones, rotación de mesas, pedidos por QR desde el móvil, reacciones en vivo y reportes. Para bares, restaurantes y eventos. Regístrate gratis.',
    ogImagePath: MASCOT_PNG,
  },
  en: {
    title:
      'Canto del Nabo | Karaoke queue management system for bars & events',
    description:
      'Karaoke queue management system: song queue, table rotation, QR song requests from any phone, live reactions and reports. Built for karaoke bars, restaurants and events. Sign up free.',
    ogImagePath: MASCOT_PNG,
  },
};

/** Keywords por idioma para structured data. */
export const LANDING_KEYWORDS: Record<AppLanguage, string> = {
  es: 'sistema de administración de turnos para karaoke, software de karaoke México, administrador de turnos y mesas de karaoke, cola de canciones de karaoke, app para pedir canciones por QR, karaoke para bares y restaurantes',
  en: 'karaoke queue management system, karaoke rotation software, song request app for bars, live karaoke management software, QR code karaoke song requests, karaoke night software for bars and restaurants',
};

/** Backwards-compat: SEO de la home en espanol. */
export const LANDING_SEO = LANDING_SEO_BY_LANG.es;

export function absoluteUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${LANDING_SITE_URL}${normalized}`;
}

function ogLocale(lang: AppLanguage): string {
  return lang === 'en' ? 'en_US' : 'es_MX';
}

function htmlLang(lang: AppLanguage): string {
  return lang === 'en' ? 'en' : 'es-MX';
}

function upsertMeta(
  attribute: 'name' | 'property',
  key: string,
  content: string
): void {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  const selector = `link[rel="${rel}"]`;
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function upsertAlternate(hreflang: string, href: string): void {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', hreflang);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

/**
 * Aplica el SEO (title, description, canonical, OG, Twitter y hreflang) de una
 * pagina publica para el idioma indicado. El idioma de cada pagina lo determina
 * su URL (`/` = es, `/en` = en).
 */
export function applyPageSeo(
  pageKey: LandingPageKey,
  lang: AppLanguage,
  seo: PageSeo
): void {
  const paths = LANDING_PAGE_PATHS[pageKey];
  const canonical = absoluteUrl(paths[lang]);
  const ogImage = absoluteUrl(seo.ogImagePath ?? MASCOT_PNG);

  document.title = seo.title;
  document.documentElement.lang = htmlLang(lang);

  upsertMeta('name', 'description', seo.description);
  upsertMeta('name', 'robots', 'index, follow');
  upsertLink('canonical', canonical);

  upsertAlternate('es-MX', absoluteUrl(paths.es));
  upsertAlternate('en', absoluteUrl(paths.en));
  upsertAlternate('x-default', absoluteUrl(paths.es));

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:locale', ogLocale(lang));
  upsertMeta('property', 'og:site_name', 'Canto del Nabo');
  upsertMeta('property', 'og:title', seo.title);
  upsertMeta('property', 'og:description', seo.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta(
    'property',
    'og:image:alt',
    'Canto del Nabo — gestión de karaoke con mascota nabo rockero'
  );

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', seo.title);
  upsertMeta('name', 'twitter:description', seo.description);
  upsertMeta('name', 'twitter:image', ogImage);
}

/** Aplica el SEO de la home segun idioma. */
export function applyLandingPageSeo(lang: AppLanguage = 'es'): void {
  applyPageSeo('home', lang, LANDING_SEO_BY_LANG[lang]);
}

function removeAlternates(): void {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((element) => element.remove());
}

export interface SimplePageSeo {
  /** Ruta publica (sin dominio) que sera el canonical, p.ej. `/ayuda`. */
  path: string;
  title: string;
  description: string;
  /** Si es true la pagina no debe indexarse (login, recuperar contraseña, 404...). */
  noindex?: boolean;
  lang?: AppLanguage;
}

/**
 * SEO para paginas publicas sin variante por idioma en la URL (ayuda, registro,
 * login, 404...). Sin esto heredan el title/description/canonical de la home
 * que viene en `index.html`, y Google las trata como duplicados de la home.
 * Quita los hreflang heredados porque estas paginas no tienen URL alterna.
 */
export function applySimplePageSeo(seo: SimplePageSeo): void {
  const lang = seo.lang ?? 'es';
  const canonical = absoluteUrl(seo.path);
  const ogImage = absoluteUrl(MASCOT_PNG);

  document.title = seo.title;
  document.documentElement.lang = htmlLang(lang);

  upsertMeta('name', 'description', seo.description);
  upsertMeta('name', 'robots', seo.noindex ? 'noindex, follow' : 'index, follow');
  upsertLink('canonical', canonical);
  removeAlternates();

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:locale', ogLocale(lang));
  upsertMeta('property', 'og:site_name', 'Canto del Nabo');
  upsertMeta('property', 'og:title', seo.title);
  upsertMeta('property', 'og:description', seo.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', ogImage);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', seo.title);
  upsertMeta('name', 'twitter:description', seo.description);
  upsertMeta('name', 'twitter:image', ogImage);
}

export function buildLandingJsonLd(
  faqs: FAQItem[],
  lang: AppLanguage = 'es',
  pageKey: LandingPageKey = 'home',
  seo: PageSeo = LANDING_SEO_BY_LANG[lang]
): string {
  const inLanguage = lang === 'en' ? 'en' : 'es-MX';
  const pageUrl = absoluteUrl(LANDING_PAGE_PATHS[pageKey][lang]);
  const graph = [
    {
      '@type': 'Organization',
      '@id': `${LANDING_SITE_URL}/#organization`,
      name: 'Canto del Nabo',
      alternateName: 'CantoNabo',
      url: LANDING_SITE_URL,
      logo: absoluteUrl(MASCOT_PNG),
      email: 'luis.ruiz@kepler.com.mx',
      areaServed: 'MX',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'MX',
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${LANDING_SITE_URL}/#website`,
      url: LANDING_SITE_URL,
      name: 'Canto del Nabo',
      description: seo.description,
      publisher: { '@id': `${LANDING_SITE_URL}/#organization` },
      inLanguage,
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${LANDING_SITE_URL}/#software`,
      name: 'Canto del Nabo',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Karaoke management software',
      operatingSystem: 'Web',
      description: seo.description,
      keywords: LANDING_KEYWORDS[lang],
      inLanguage,
      image: absoluteUrl(seo.ogImagePath ?? MASCOT_PNG),
      url: pageUrl,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'MXN',
        description:
          lang === 'en'
            ? 'Free sign-up; per-event or monthly subscription plans'
            : 'Registro gratuito; planes por evento o suscripción mensual',
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      inLanguage,
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
}

/** Convierte URL embed de YouTube a enlace público para crawlers */
export function youtubeWatchUrl(embedUrl: string): string {
  const match = embedUrl.match(/embed\/([^?&]+)/);
  if (match?.[1]) {
    return `https://www.youtube.com/watch?v=${match[1]}`;
  }
  return embedUrl;
}

export function youtubeVideoIdFromUrl(url: string): string | null {
  const patterns = [/embed\/([^?&/]+)/, /[?&]v=([^?&/]+)/, /youtu\.be\/([^?&/]+)/];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
}

/** Miniatura pública de YouTube (la misma que muestra la plataforma) */
export function youtubeThumbnailUrl(
  videoUrl: string,
  quality: 'maxresdefault' | 'hqdefault' = 'hqdefault'
): string {
  const id = youtubeVideoIdFromUrl(videoUrl);
  return id ? `https://img.youtube.com/vi/${id}/${quality}.jpg` : '';
}
